/**
 * The two markers the mailbox connect flow leaves on the CRM page it returns to (F20-i Wave-1 contract
 * § Redirect Contract), which replace F20-a's fixed return to `/email/mailbox` with its own marker:
 *
 * - `mailboxConnect` — `success` or `failed`;
 * - `mailboxConnectReason` — with `failed` only, Microsoft's OpenID Connect `error` upper-cased, from a
 *   closed vocabulary.
 *
 * Pure, so the contract's tolerance rules are unit tests rather than router-only assertions: any other
 * `mailboxConnect` value is no marker at all, and an unrecognised reason falls back to the message
 * without a reason. The same message and reason copy serve the stub path's `409 MAILBOX_CONNECT_FAILED`,
 * which the contract makes identical to the `failed` marker (F20-i WI-1.3 plan D-8).
 */

/** The marker parameter. */
export const CONNECT_MARKER_PARAM = 'mailboxConnect';
/** The reason parameter, beside `mailboxConnect=failed` only. */
export const CONNECT_REASON_PARAM = 'mailboxConnectReason';

/** What the flow reported. `reason` is the raw parameter (or `null`); the copy is chosen later. */
export type ConnectMarker =
    | { readonly kind: 'success' }
    | { readonly kind: 'failed'; readonly reason: string | null };

/**
 * Read the marker, or `undefined` when the page carries none this client recognises. Exactly `success`
 * and `failed` are honoured; an empty or other value is no marker.
 */
export function readConnectMarker(params: URLSearchParams): ConnectMarker | undefined {
    const value = params.get(CONNECT_MARKER_PARAM);
    if (value === 'success') {
        return { kind: 'success' };
    }
    if (value === 'failed') {
        const reason = params.get(CONNECT_REASON_PARAM);
        return { kind: 'failed', reason: reason === null || reason === '' ? null : reason };
    }
    return undefined;
}

/** A copy of `params` without either marker parameter, every other parameter kept in order. */
export function withoutConnectMarkers(params: URLSearchParams): URLSearchParams {
    const next = new URLSearchParams(params);
    next.delete(CONNECT_MARKER_PARAM);
    next.delete(CONNECT_REASON_PARAM);
    return next;
}

/** The failure message, fixed by the spec (F20-i FR-17, FR-19). One sentence, so no full stop (§9). */
export const CONNECT_FAILED_MESSAGE = 'Your mailbox could not be connected';

/**
 * The reason in plain words, per value of the contract's `mailboxConnectReason` vocabulary (the
 * developer's wording at implementation, recorded in the plan's hand-off). `satisfies` keeps the table
 * to the vocabulary.
 */
const REASON_COPY = {
    INTERACTION_REQUIRED: 'Microsoft needs you to sign in again',
    LOGIN_REQUIRED: 'Microsoft needs you to sign in again',
    CONSENT_REQUIRED: 'Microsoft needs your permission to connect your mailbox',
    ACCOUNT_SELECTION_REQUIRED: 'Microsoft needs you to choose an account',
    ACCESS_DENIED: 'Permission to connect your mailbox was declined',
} as const satisfies Record<string, string>;

function isKnownReason(reason: string): reason is keyof typeof REASON_COPY {
    return Object.hasOwn(REASON_COPY, reason);
}

/** The reason's plain words, or `null` for no reason and for one this build does not know. */
export function connectReasonCopy(reason: string | null): string | null {
    return reason !== null && isKnownReason(reason) ? REASON_COPY[reason] : null;
}

/**
 * The whole failure text: the message alone, or the message and the reason as two sentences, each with
 * its full stop (§9: two or more sentences keep theirs).
 */
export function connectFailureText(reason: string | null): string {
    const copy = connectReasonCopy(reason);
    return copy === null ? CONNECT_FAILED_MESSAGE : `${CONNECT_FAILED_MESSAGE}. ${copy}.`;
}
